import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { FieldHelp } from '@/components/forms/FieldHelp';
import { cn } from '@/lib/utils';
import { guidePage } from '@/config/guide/registry';
import {
  fieldDefKey,
  fieldLabelKey,
  fieldNegKey,
  fieldPosKey,
  type FieldDoc,
} from './guide-types.js';

function findField(pageId: string, fieldKey: string): FieldDoc | undefined {
  return guidePage(pageId)?.fields.find((f) => f.key === fieldKey);
}

/**
 * Compose one field's tooltip: definition, its formula, then — for signed
 * money — what each direction of the sign actually means in business terms.
 *
 * The sign lines are the whole point. `memberWinloss` renders red when it is
 * negative, and negative means the MEMBERS lost, i.e. the house won. Without
 * both directions spelled out the colour actively misleads.
 */
function helpText(pageId: string, field: FieldDoc, t: (k: string) => string): string {
  const lines = [t(fieldDefKey(pageId, field.key))];
  if (field.formula) lines.push(field.formula);
  if (field.kind === 'money-signed') {
    lines.push('', `+  ${t(fieldPosKey(pageId, field.key))}`, `−  ${t(fieldNegKey(pageId, field.key))}`);
  }
  return lines.join('\n');
}

/** The small `i` beside a column header or KPI label. */
export function FieldGuide({ pageId, fieldKey }: { pageId: string; fieldKey: string }) {
  const { t } = useTranslation('guide');
  const field = findField(pageId, fieldKey);
  if (!field) return null;
  return (
    <FieldHelp
      text={helpText(pageId, field, t)}
      ariaLabel={t(fieldLabelKey(pageId, fieldKey))}
    />
  );
}

/** A DataTable column header carrying its own `i`. Pass translated header text. */
export function GuideHeader({
  pageId,
  fieldKey,
  children,
}: {
  pageId: string;
  fieldKey: string;
  children: React.ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      {children}
      <FieldGuide pageId={pageId} fieldKey={fieldKey} />
    </span>
  );
}

/** A KPI/section label with its `i`, for cards outside a DataTable. */
export function GuideLabel({
  pageId,
  fieldKey,
  children,
}: {
  pageId: string;
  fieldKey: string;
  children: React.ReactNode;
}) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      <span className="truncate">{children}</span>
      <FieldGuide pageId={pageId} fieldKey={fieldKey} />
    </span>
  );
}

/**
 * Always-visible colour key for a sign-coloured money column.
 *
 * Deliberately not hidden behind the guide button: the red/green convention is
 * read hundreds of times a session, it is inverted between screens in this
 * product, and looking it up even once is once too many.
 */
export function SignLegend({
  pageId,
  fieldKey,
  className,
}: {
  pageId: string;
  fieldKey: string;
  className?: string;
}) {
  const { t } = useTranslation('guide');
  const field = findField(pageId, fieldKey);
  if (!field || field.kind !== 'money-signed') return null;
  return (
    <p className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground', className)}>
      <span>{t('common.legendPrefix')}</span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-block h-2 w-2 rounded-sm bg-emerald-500" aria-hidden />
        <span className="text-emerald-600 dark:text-emerald-400">+</span>
        {t(fieldPosKey(pageId, fieldKey))}
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-block h-2 w-2 rounded-sm bg-destructive" aria-hidden />
        <span className="text-destructive">−</span>
        {t(fieldNegKey(pageId, fieldKey))}
      </span>
    </p>
  );
}

/**
 * Permanent banner on a screen that does not do what it appears to do.
 *
 * `records-intent` screens save an operator's decision that no service ever
 * reads — an IP deny rule that blocks nobody, a maintenance window players
 * never see. Believing such a control worked is worse than not having it, so
 * this is on the page itself and never behind the guide button.
 */
export function PageStatusBanner({ pageId }: { pageId: string }) {
  const { t } = useTranslation('guide');
  const page = guidePage(pageId);
  if (!page || page.status === 'live') return null;
  const key = page.status === 'records-intent' ? 'common.recordsIntent' : 'common.partiallyWired';
  return (
    <div
      role="status"
      className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
      <span>
        <span className="font-medium">{t(key)}</span>{' '}
        <span className="text-muted-foreground">{t(`${pageId}.statusNote`)}</span>
      </span>
    </div>
  );
}
